import type { QueryParams } from '@/types/api';

/**
 * Fábrica de query keys de TanStack Query.
 *
 * Regla de oro: todas las keys empiezan por el `tenantId` (excepto `auth`), así al cambiar
 * de tenant activo se puede invalidar por prefijo (`['tenants', tenantId]`) sin tocar las
 * queries de los demás negocios y sin fugas de caché entre tenants.
 *
 * Las pantallas nuevas deben agregar aquí su key antes de usarla.
 */
export const queryKeys = {
  /** Usuario autenticado (`GET /api/auth/me`). */
  auth: {
    me: () => ['auth', 'me'] as const,
  },

  /** Tenants del usuario y sus miembros (módulo 1). */
  tenants: {
    all: () => ['tenants'] as const,
    detail: (tenantId: string) => ['tenants', tenantId] as const,
    members: (tenantId: string) => ['tenants', tenantId, 'members'] as const,
  },

  /** Ajustes generales del negocio (módulo 2). */
  settings: (tenantId: string) => ['tenants', tenantId, 'settings'] as const,

  /** Vista previa del prompt final del negocio (módulo 2, 22-sep-2026). */
  settingsPromptFinal: (tenantId: string, asistente: string) =>
    ['tenants', tenantId, 'settings', 'prompt-final', asistente] as const,

  /** Métodos de pago (módulo 2). */
  paymentMethods: (tenantId: string) => ['tenants', tenantId, 'payment-methods'] as const,

  /** Pago en línea con Wompi (docs/WOMPI.md): estado de la conexión del negocio. */
  wompi: (tenantId: string) => ['tenants', tenantId, 'wompi'] as const,

  /** Integración con Dropi (docs/DROPI.md): conexión, geografía y catálogo del proveedor. */
  dropi: {
    /** Estado de la conexión (token/origen/activo). */
    status: (tenantId: string) => ['tenants', tenantId, 'dropi', 'status'] as const,
    /** Geografía oficial: `[tenantId, 'dropi', 'geografia', departamento]`. */
    geografia: (tenantId: string, departamento?: string) =>
      ['tenants', tenantId, 'dropi', 'geografia', departamento ?? ''] as const,
    /** Catálogo de Dropi para vincular productos. */
    productos: (tenantId: string, startData: number) =>
      ['tenants', tenantId, 'dropi', 'productos', startData] as const,
    /** UN producto del catálogo por su id de Dropi (vinculación directa). */
    producto: (tenantId: string, productId: number) =>
      ['tenants', tenantId, 'dropi', 'productos', 'id', productId] as const,
    /** Huella digital de un teléfono. */
    huella: (tenantId: string, telefono: string) =>
      ['tenants', tenantId, 'dropi', 'huella', telefono] as const,
    /** Previsualización del envío de una orden. */
    previsualizacion: (tenantId: string, orderId: string) =>
      ['tenants', tenantId, 'dropi', 'previsualizacion', orderId] as const,
  },

  /** Integraciones: API keys y canales (módulo 2). */
  integrations: {
    overview: (tenantId: string) => ['tenants', tenantId, 'integrations'] as const,
    channels: (tenantId: string) => ['tenants', tenantId, 'integrations', 'channels'] as const,
    /** Pages candidatas de una sesión OAuth pendiente (módulo 2.1), por `ticket`. */
    oauthPending: (tenantId: string, ticket: string) =>
      ['tenants', tenantId, 'integrations', 'oauth-pending', ticket] as const,
  },

  /** Catálogo de productos (módulo 3). */
  products: {
    /**
     * Prefijo de TODO el módulo (listado con cualquier filtro + el detalle de una fila). Hace falta
     * además de `list` porque `list(tenantId)` es `[..., {}]`, que NO es prefijo de
     * `[..., { activo: true }]`: para invalidar «todo el catálogo» después de una escritura que toca
     * varios productos (la sincronización del stock de Dropi) se usa esta key.
     */
    all: (tenantId: string) => ['tenants', tenantId, 'products'] as const,
    list: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'products', filters ?? {}] as const,
    detail: (tenantId: string, productId: string) =>
      ['tenants', tenantId, 'products', productId] as const,
  },

  /** Inbox: conversaciones y su historial (módulo 5). */
  conversations: {
    /** Prefijo de todo el módulo: invalida de un golpe listado, detalle e historial. */
    all: (tenantId: string) => ['tenants', tenantId, 'conversations'] as const,
    /**
     * Prefijo de los LISTADOS (todas las combinaciones de filtros). Hace falta además de `list`
     * porque `list(tenantId)` sin filtros es `[..., 'list', {}]`, que no es prefijo de
     * `[..., 'list', { orden: 'creacion' }]`: para invalidar/reescribir "todos los listados"
     * se usa esta key.
     */
    lists: (tenantId: string) => ['tenants', tenantId, 'conversations', 'list'] as const,
    list: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'conversations', 'list', filters ?? {}] as const,
    detail: (tenantId: string, conversationId: string) =>
      ['tenants', tenantId, 'conversations', 'detail', conversationId] as const,
    /**
     * Historial del chat: UNA sola key por conversación aunque la query sea infinita
     * (`useInfiniteQuery` guarda todas las páginas bajo esta misma key).
     */
    messages: (tenantId: string, conversationId: string) =>
      ['tenants', tenantId, 'conversations', 'messages', conversationId] as const,
  },

  /** Órdenes (módulo 7). */
  orders: {
    /** Prefijo de todo el módulo: invalida de un golpe listado y detalle. */
    all: (tenantId: string) => ['tenants', tenantId, 'orders'] as const,
    /**
     * Prefijo de los LISTADOS (todas las combinaciones de filtros). Hace falta además de `list`
     * porque `list(tenantId)` es `[..., 'list', {}]`, que no es prefijo de
     * `[..., 'list', { estado: 'ENVIADA' }]`: para invalidar «todos los listados» se usa esta key.
     * (El listado de órdenes SÍ tiene filtros: `estado`, `conversationId` y `limit`.)
     */
    lists: (tenantId: string) => ['tenants', tenantId, 'orders', 'list'] as const,
    list: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'orders', 'list', filters ?? {}] as const,
    detail: (tenantId: string, orderId: string) =>
      ['tenants', tenantId, 'orders', 'detail', orderId] as const,
  },

  /** CRM: kanban y leads (módulo 9). */
  crm: {
    /**
     * Prefijo de TODO el CRM (`kanban`, `leads` y el detalle de un lead).
     *
     * Hace falta además de `kanban` / `leads` porque esas keys incluyen los filtros
     * (`kanban(tenantId)` es `[..., 'kanban', {}]`, que NO es prefijo de
     * `[..., 'kanban', { limitePorColumna: 30 }]`): para invalidar «todo el CRM» tras una
     * reclasificación o un recálculo se usa esta key.
     */
    all: (tenantId: string) => ['tenants', tenantId, 'crm'] as const,
    kanban: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'crm', 'kanban', filters ?? {}] as const,
    leads: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'crm', 'leads', filters ?? {}] as const,
    lead: (tenantId: string, conversationId: string) =>
      ['tenants', tenantId, 'crm', 'leads', conversationId] as const,
  },

  /** Plantillas de WhatsApp (módulo 8). */
  templates: {
    /**
     * Prefijo de TODO el módulo (el listado con cualquier filtro, el catálogo de eventos y el detalle
     * de una fila).
     *
     * Hace falta además de `list` porque `list(tenantId)` es `[..., {}]`, que NO es prefijo de
     * `[..., { estado: 'BORRADOR' }]`: para invalidar «todas las plantillas» después de una escritura
     * (crear, editar, borrar, sembrar, sincronizar) se usa esta key. Sirve para el detalle y para el
     * catálogo porque ambos agregan un string en la misma posición y un id nunca es un objeto.
     */
    all: (tenantId: string) => ['tenants', tenantId, 'whatsapp-templates'] as const,
    list: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'whatsapp-templates', filters ?? {}] as const,
    detail: (tenantId: string, templateId: string) =>
      ['tenants', tenantId, 'whatsapp-templates', templateId] as const,
    /**
     * Catálogo de eventos (qué plantilla recomienda el sistema para cada evento y qué campos puede
     * usar). El endpoint es de solo lectura y no cambia salvo por las escrituras del propio módulo.
     */
    eventos: (tenantId: string) => ['tenants', tenantId, 'whatsapp-templates', 'eventos'] as const,
  },

  /** Campañas/anuncios y su gasto (módulo 10). */
  campaigns: {
    /**
     * Prefijo de TODO el módulo (el listado con cualquier filtro + el detalle de una fila).
     *
     * Hace falta además de `list` porque `list(tenantId)` es `[..., 'campaigns', {}]`, que NO es
     * prefijo de `[..., 'campaigns', { activo: 'true' }]`: para invalidar «todo el gasto» después de
     * una escritura (crear, editar, borrar, descubrir) se usa esta key. Sirve para las dos porque el
     * detalle agrega el `campaignId` en la misma posición, y un id nunca es un objeto de filtros.
     */
    all: (tenantId: string) => ['tenants', tenantId, 'campaigns'] as const,
    list: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'campaigns', filters ?? {}] as const,
    detail: (tenantId: string, campaignId: string) =>
      ['tenants', tenantId, 'campaigns', campaignId] as const,
  },

  /** Reportes (módulo 10). */
  reports: {
    resumen: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'reports', 'resumen', filters ?? {}] as const,
    campanas: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'reports', 'campanas', filters ?? {}] as const,
    serie: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'reports', 'serie', filters ?? {}] as const,
    horarios: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'reports', 'horarios', filters ?? {}] as const,
    productos: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'reports', 'productos', filters ?? {}] as const,
    motivosNoCierre: (tenantId: string, filters?: QueryParams) =>
      ['tenants', tenantId, 'reports', 'motivos-no-cierre', filters ?? {}] as const,
  },
} as const;
